import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Divider, Grid, Skeleton, Typography } from '@mui/material';
import { CheckCircleOutlined, CheckRounded, LocationOn } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import SectionContainer from '../components/SectionContainer';
import RevealOnScroll from '../components/RevealOnScroll';

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(
  /\/api\/?$/,
  ''
);

const CHECKLIST_MAIN = [
  'Your own URL — bookmylibrary.in/l/your-library',
  '5 premium themes — Modern, Classic, Minimal, Vibrant, Professional',
  'Built-in photo gallery & amenities showcase',
  'Auto-listed plans, prices, and contact form',
  'SEO-optimized for Google search in your city',
  'Mobile-first, blazingly fast, fully managed',
];

const CHECKLIST_PRICE = [
  'Custom branded URL',
  'Choice of 5 themes',
  'Unlimited content updates',
  'Hosting & support included',
];

function resolveImageUrl(raw) {
  if (!raw) return null;
  if (raw.startsWith('/uploads/')) return `${API_ORIGIN}${raw}`;
  return raw;
}

function BrowserMockupSkeleton() {
  return (
    <Box
      sx={{
        borderRadius: '16px',
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Skeleton variant="rectangular" height={36} />
      <Skeleton variant="rectangular" sx={{ width: '100%', paddingTop: '62.5%', height: 0 }} />
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Skeleton variant="text" width="65%" height={22} />
        <Skeleton variant="text" width="40%" height={16} />
        <Skeleton variant="text" width="30%" height={16} sx={{ ml: 'auto' }} />
      </Box>
    </Box>
  );
}

function BrowserCard({ library, index }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const orange = theme.palette.brand.orange.main;

  const imageUrl = resolveImageUrl(
    library.gallery && library.gallery.length > 0 ? library.gallery[0] : null
  );

  const cardBg = isDark ? 'rgba(27,54,93,0.55)' : theme.palette.background.paper;
  const cardBorder = isDark
    ? '1px solid rgba(255,255,255,0.08)'
    : '1px solid rgba(27,54,93,0.08)';
  const barBg = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(27,54,93,0.04)';
  const addrBg = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(27,54,93,0.06)';
  const firstLetter = library.name ? library.name.charAt(0).toUpperCase() : 'L';

  return (
    <RevealOnScroll delay={Math.min(index * 0.08, 0.5)}>
      <motion.a
        href={`/l/${library.website_slug}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration: 'none', display: 'block', height: '100%' }}
        whileHover={{ y: -6 }}
        transition={{ duration: 0.22, ease: 'easeOut' }}
        aria-label={`Visit ${library.name} website`}
      >
        <Box
          sx={{
            borderRadius: '16px',
            background: cardBg,
            border: cardBorder,
            boxShadow: isDark
              ? '0 4px 20px rgba(0,0,0,0.25)'
              : '0 4px 20px rgba(27,54,93,0.07)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            transition: 'box-shadow 0.22s ease',
            '&:hover': {
              boxShadow: isDark
                ? '0 20px 40px -8px rgba(0,0,0,0.45), 0 0 0 1px rgba(242,101,34,0.25)'
                : '0 20px 40px -16px rgba(242,101,34,0.3), 0 0 0 1px rgba(242,101,34,0.15)',
            },
          }}
        >
          {/* Browser top bar */}
          <Box
            sx={{
              px: 1.5,
              py: 0.75,
              background: barBg,
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              flexShrink: 0,
              borderBottom: isDark
                ? '1px solid rgba(255,255,255,0.05)'
                : '1px solid rgba(27,54,93,0.06)',
            }}
          >
            {/* Traffic lights */}
            <Box sx={{ display: 'flex', gap: '5px', alignItems: 'center', flexShrink: 0 }}>
              {['#FF5F57', '#FEBC2E', '#28C840'].map((color) => (
                <Box
                  key={color}
                  aria-hidden="true"
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: color,
                    opacity: 0.85,
                  }}
                />
              ))}
            </Box>

            {/* Address bar */}
            <Box
              sx={{
                flex: 1,
                mx: 0.5,
                px: 1,
                py: 0.3,
                borderRadius: '999px',
                background: addrBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 0,
              }}
            >
              <Typography
                variant="caption"
                component="span"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.6rem',
                  color: 'text.secondary',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                aria-hidden="true"
              >
                bookmylibrary.in/l/{library.website_slug}
              </Typography>
            </Box>
          </Box>

          {/* Preview image area — 16:10 */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              paddingTop: '62.5%',
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            {imageUrl ? (
              <Box
                component="img"
                src={imageUrl}
                alt={`${library.name} library website preview`}
                loading="lazy"
                decoding="async"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background: `linear-gradient(135deg, ${theme.palette.brand.navy.dark}, ${theme.palette.brand.navy.main})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  aria-hidden="true"
                  sx={{
                    fontSize: '4rem',
                    fontWeight: 800,
                    color: 'rgba(255,255,255,0.12)',
                    userSelect: 'none',
                    lineHeight: 1,
                  }}
                >
                  {firstLetter}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Card body */}
          <Box
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
              flexGrow: 1,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: '0.9rem',
                color: 'text.primary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {library.name}
            </Typography>

            {library.city && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LocationOn sx={{ fontSize: 13, color: 'text.secondary' }} aria-hidden="true" />
                <Typography variant="caption" color="text.secondary">
                  {library.city}
                </Typography>
              </Box>
            )}

            <Box sx={{ mt: 'auto', pt: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Typography
                variant="caption"
                sx={{
                  color: orange,
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  letterSpacing: '0.01em',
                }}
              >
                Visit live site →
              </Typography>
            </Box>
          </Box>
        </Box>
      </motion.a>
    </RevealOnScroll>
  );
}

BrowserCard.propTypes = {
  library: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string.isRequired,
    city: PropTypes.string,
    gallery: PropTypes.arrayOf(PropTypes.string),
    website_slug: PropTypes.string.isRequired,
  }).isRequired,
  index: PropTypes.number.isRequired,
};

function WebsiteAddonSection({ libraries, loading }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const orange = theme.palette.brand.orange.main;
  const golden = theme.palette.brand.golden.main;

  const cardBg = isDark ? 'rgba(27,54,93,0.6)' : theme.palette.background.paper;
  const cardBorder = isDark
    ? '1px solid rgba(255,255,255,0.08)'
    : '1px solid rgba(27,54,93,0.08)';

  const handleAddToSignup = () => {
    const el = document.getElementById('signup');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    window.dispatchEvent(new CustomEvent('bml:toggle-website-addon', { detail: true }));
  };

  return (
    <SectionContainer
      id="library-website"
      sx={{
        background: isDark
          ? `radial-gradient(ellipse at 20% 60%, rgba(242,101,34,0.05) 0%, transparent 65%), ${theme.palette.background.default}`
          : `radial-gradient(ellipse at 20% 60%, rgba(242,101,34,0.04) 0%, transparent 65%), ${theme.palette.background.default}`,
      }}
    >
      {/* Row 1: The pitch */}
      <RevealOnScroll>
        <Grid
          container
          spacing={{ xs: 3, md: 5 }}
          alignItems="stretch"
          sx={{ mb: { xs: 6, md: 8 } }}
        >
          {/* Left: feature list */}
          <Grid item xs={12} md={7}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              {/* Eyebrow chip */}
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  px: 1.5,
                  py: 0.4,
                  mb: 2.5,
                  borderRadius: '999px',
                  bgcolor: isDark ? 'rgba(242,101,34,0.15)' : 'rgba(242,101,34,0.1)',
                  border: `1px solid ${isDark ? 'rgba(242,101,34,0.3)' : 'rgba(242,101,34,0.25)'}`,
                  width: 'fit-content',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: orange,
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                  }}
                >
                  Optional upgrade
                </Typography>
              </Box>

              <Typography
                variant="h3"
                component="h3"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  mb: 1.5,
                  fontSize: { xs: '1.55rem', md: '2rem' },
                  lineHeight: 1.2,
                }}
              >
                Your library, beautifully online.
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.75,
                  mb: 3.5,
                  maxWidth: 520,
                }}
              >
                A premium single-page website for your library — your brand, your photos, your own URL.
                Fully managed by our team.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {CHECKLIST_MAIN.map((item) => (
                  <Box key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <CheckCircleOutlined
                      sx={{ color: orange, fontSize: 20, flexShrink: 0, mt: 0.15 }}
                      aria-hidden="true"
                    />
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.55 }}>
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Grid>

          {/* Right: price card */}
          <Grid item xs={12} md={5}>
            <RevealOnScroll delay={0.12}>
              <Box
                sx={{
                  p: { xs: 3.5, md: 4.5 },
                  borderRadius: '24px',
                  background: isDark
                    ? `linear-gradient(145deg, rgba(242,101,34,0.12) 0%, rgba(27,54,93,0.75) 100%)`
                    : `linear-gradient(145deg, rgba(242,101,34,0.06) 0%, rgba(255,255,255,1) 100%)`,
                  border: isDark
                    ? '1px solid rgba(242,101,34,0.22)'
                    : '1px solid rgba(242,101,34,0.18)',
                  boxShadow: isDark
                    ? '0 12px 48px rgba(0,0,0,0.35)'
                    : '0 12px 48px rgba(242,101,34,0.12)',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  transition: 'box-shadow 0.25s ease',
                  '&:hover': {
                    boxShadow: isDark
                      ? '0 20px 60px rgba(0,0,0,0.45)'
                      : '0 20px 60px rgba(242,101,34,0.2)',
                  },
                }}
              >
                {/* Label */}
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 700,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    fontSize: '0.68rem',
                    mb: 2,
                    display: 'block',
                  }}
                >
                  Library Website
                </Typography>

                {/* Price row */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 0.75,
                    mb: 2.5,
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: '2.75rem', md: '3rem' },
                      lineHeight: 1,
                      color: orange,
                      fontFamily: theme.typography.fontFamily,
                    }}
                  >
                    ₹1,999
                  </Typography>
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{ color: 'text.secondary', fontWeight: 500 }}
                  >
                    / year
                  </Typography>
                </Box>

                <Divider
                  sx={{
                    mb: 2.5,
                    borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(27,54,93,0.08)',
                  }}
                />

                {/* Mini checklist */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 3.5 }}>
                  {CHECKLIST_PRICE.map((item) => (
                    <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckRounded
                        sx={{
                          color: orange,
                          fontSize: 17,
                          flexShrink: 0,
                        }}
                        aria-hidden="true"
                      />
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.83rem' }}>
                        {item}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* CTA button */}
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={handleAddToSignup}
                  sx={{
                    mt: 'auto',
                    background: `linear-gradient(135deg, ${orange}, ${golden})`,
                    color: '#fff',
                    fontWeight: 700,
                    py: 1.6,
                    fontSize: '0.95rem',
                    borderRadius: 3,
                    boxShadow: '0 8px 24px rgba(242,101,34,0.35)',
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.brand.orange.light}, ${orange})`,
                      boxShadow: '0 12px 32px rgba(242,101,34,0.48)',
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  Add this to my signup
                </Button>
              </Box>
            </RevealOnScroll>
          </Grid>
        </Grid>
      </RevealOnScroll>

      {/* Row 2: Live examples */}
      <Box>
        {/* Sub-header */}
        <RevealOnScroll>
          <Box sx={{ mb: { xs: 3.5, md: 5 } }}>
            <Typography
              variant="overline"
              sx={{
                color: orange,
                fontWeight: 700,
                letterSpacing: '0.12em',
                fontSize: '0.7rem',
                display: 'block',
                mb: 0.75,
              }}
            >
              Live examples
            </Typography>
            <Typography
              variant="h5"
              component="h4"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                mb: 0.75,
                fontSize: { xs: '1.2rem', md: '1.4rem' },
              }}
            >
              See real library websites on BookMyLibrary.
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              These libraries have already claimed their spot. Yours could be next.
            </Typography>
          </Box>
        </RevealOnScroll>

        {loading ? (
          <Grid container spacing={3}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Grid item xs={12} sm={6} md={4} key={`ws-skeleton-${i}`}>
                <BrowserMockupSkeleton />
              </Grid>
            ))}
          </Grid>
        ) : libraries.length === 0 ? (
          <RevealOnScroll>
            <Box
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: '16px',
                border: isDark
                  ? '1px dashed rgba(255,255,255,0.12)'
                  : '1px dashed rgba(27,54,93,0.15)',
                background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(27,54,93,0.02)',
                textAlign: 'center',
                maxWidth: 560,
                mx: 'auto',
              }}
            >
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.75 }}>
                Your library could be one of the first premium sites on the platform. Sign up below
                to claim your spot.
              </Typography>
            </Box>
          </RevealOnScroll>
        ) : (
          <Grid container spacing={3}>
            {libraries.map((library, index) => (
              <Grid item xs={12} sm={6} md={4} key={library.id || index}>
                <BrowserCard library={library} index={index} />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </SectionContainer>
  );
}

WebsiteAddonSection.propTypes = {
  libraries: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string.isRequired,
      city: PropTypes.string,
      gallery: PropTypes.arrayOf(PropTypes.string),
      website_slug: PropTypes.string.isRequired,
    })
  ).isRequired,
  loading: PropTypes.bool.isRequired,
};

export default WebsiteAddonSection;
